import React, { useEffect, useMemo, useState } from "react"
import PropTypes from "prop-types"
import { Link } from "gatsby"
import "./header.scss"
import "./menu-modal.scss"
import { StaticImage } from "gatsby-plugin-image"
import { Modal } from "react-bootstrap"
import { getCurrentLang, isBrowser } from "../../services/Utils"
import { LANGUAGES } from "../../constants/languages"
import { SOCIAL_LINKS } from "../../constants/social-links"

import HeaderItemPopup from "../../shared/header-item-popup/HeaderItemPopup"

const Header = ({ menu, displayLogoLinks, isModal, closeModalFunction, contents }) => {
  const displayLogoLinksClass = displayLogoLinks ? "links-active" : ""

  const [show, setShow] = useState(false)
  const [currLang, setCurrLang] = useState(undefined)

  useEffect(() => {
    setCurrLang(getCurrentLang())
  }, [])

  const sortedMenu = useMemo(() => {
    if (!menu) return []
    const sorted = [...menu].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
    const grouped = []
    for (let i = 0; i < sorted.length; i++) {
      if (sorted[i].level === 1) {
        grouped.push({ ...sorted[i], popup: [] })
      } else if (grouped.length > 0) {
        grouped[grouped.length - 1].popup.push(sorted[i])
      }
    }
    return grouped
  }, [menu])

  const sortedFlatMenu = useMemo(() => {
    if (!menu) return []
    return [...menu].sort((a, b) => (a?.position ?? 0) - (b?.position ?? 0))
  }, [menu])

  const onChangeLang = e => {
    if (!isBrowser()) return
    const value = e.target.value
    if (value === currLang) return

    const paths = window.location.pathname.split("/").filter(Boolean)
    if (paths[0]?.length === 2) {
      paths.shift()
    }
    if (value !== "uk") {
      paths.unshift(value)
    }
    window.location.href = `/${paths.join("/")}`
  }

  const handleShow = () => setShow(true)
  const handleClose = () => setShow(false)

  const composeModalMenu = menuItems => {
    if (!menuItems?.length) return null
    return menuItems.map((item, index) => {
      if (item.level !== 1 && item.url.includes("http")) {
        return (
          <h3
            key={item.id}
            className={`${item.level === 2 ? "site-page" : ""} modal-nav-item mb-3 curs-P`}
          >
            <a href={item.url} target="_blank" rel="noopener noreferrer">
              {item.title}
            </a>
          </h3>
        )
      }
      if (
        item.level !== 1 ||
        index === menuItems.length - 1 ||
        menuItems[index + 1]?.level !== 2
      ) {
        return (
          <Link key={item.id} to={item.url} onClick={handleClose}>
            <h3
              className={`${item.level === 2 ? "site-page" : ""} modal-nav-item mb-3 curs-P`}
            >
              {item.title}
            </h3>
          </Link>
        )
      }
      return (
        <h3 key={item.id} className="modal-nav-item mb-3 curs-P">
          {item.title}
        </h3>
      )
    })
  }

  return (
    <div className="container">
      <header className="header row align-items-center">
        <div
          className={`header-content-wrapper ${displayLogoLinksClass} col-6 col-sm-6 col-lg-2`}
        >
          <div className="logo-wrapper">
            <Link to="/">
              <div className="logo curs-P"></div>
            </Link>

            <div
              className={`external-links-wrapper ${displayLogoLinksClass} justify-content-between align-item-center`}
            >
              <div className="external-link">BigBoard</div>
              <div className="external-link">|</div>
              <div className="external-link">JCDecaux</div>
            </div>
          </div>
        </div>
        <div className="col">
          <div className="navigation d-flex justify-content-between">
            {isModal ? (
              <div className="navigation__left d-flex gap-4">
                {SOCIAL_LINKS.map(link => (
                  <div
                    key={link.name}
                    className={`navigation__item ${link.name.toLowerCase()}-link`}
                  >
                    <a href={link.href} target="_blank" rel="noopener noreferrer">
                      {link.name.toLowerCase()}
                    </a>
                  </div>
                ))}
              </div>
            ) : (
              <div className="navigation__left d-flex align-items-center gap-4 visible-header-links">
                {sortedMenu.length > 0 &&
                  sortedMenu.slice(0, 5).map(menuItem => {
                    if (menuItem.popup.length > 0) {
                      return (
                        <HeaderItemPopup menuItem={menuItem} key={menuItem.id} />
                      )
                    }
                    return menuItem.url.includes("http") ? (
                      <div key={menuItem.id} className="navigation__item">
                        <a href={menuItem.url} target="_blank" rel="noopener noreferrer">
                          {menuItem.title}
                        </a>
                      </div>
                    ) : (
                      <Link key={menuItem.id} to={menuItem.url}>
                        <div className="navigation__item">{menuItem.title}</div>
                      </Link>
                    )
                  })}
              </div>
            )}
            <div className="navigation__right d-flex gap-5">
              {isModal ? (
                <div
                  className="close-btn d-flex align-items-center"
                  onClick={closeModalFunction}
                >
                  <button type="button" className="btn-close" aria-label="Close" />
                </div>
              ) : (
                <div className="navigation__item align-items-center phone-number">
                  <div
                    dangerouslySetInnerHTML={{
                      __html: contents?.["Header Phone HTML"],
                    }}
                  />
                </div>
              )}
              {!isModal && (
                <>
                  <div className="navigation__item d-flex align-items-center lang-selector">
                    <select
                      value={currLang}
                      onChange={onChangeLang}
                      className={`languages-dropdown ${currLang === "en" ? "lang-en" : ""}`}
                    >
                      {LANGUAGES.map(l => (
                        <option
                          key={l.key}
                          value={l.key}
                          disabled={l.key === currLang}
                          className="lang-option"
                        >
                          {l.label}
                        </option>
                      ))}
                    </select>

                    {currLang && (
                      <img
                        src={`/${currLang}/flags/${currLang}.svg`}
                        className="mb-0 lang-flag"
                        width={20}
                        height={20}
                        alt={`${currLang} flag`}
                      />
                    )}
                  </div>

                  <div
                    className="navigation__item d-flex align-items-center gap-2 menu-burger"
                    onClick={handleShow}
                  >
                    <StaticImage
                      src="../../icons/menu-icon.svg"
                      width={18}
                      height={12}
                      formats={["PNG"]}
                      alt="Menu icon"
                    />
                  </div>
                </>
              )}
              <Modal show={show} fullscreen onHide={handleClose}>
                <Modal.Header>
                  <div className="container">
                    <Header isModal={true} closeModalFunction={handleClose} />
                  </div>
                </Modal.Header>
                <Modal.Body>
                  <div className="container">
                    <div className="row">
                      <div className="col-lg-2" />
                      <div className="col items-list">
                        {composeModalMenu(sortedFlatMenu)}
                      </div>
                    </div>
                  </div>
                </Modal.Body>
              </Modal>
            </div>
          </div>
        </div>
      </header>
      {!isModal && (
        <div className="call-button-row row justify-content-end">
          <div className="col-12 col-md-6 col-xl-3">
            <a
              href="tel:+380445859955"
              className="call-button"
              aria-label="Зателефонувати 585 99 55"
            >
              <span className="call-button__text">ЗВ'ЯЖИСЬ</span>
              <span className="call-button__number">585 99 55</span>
              <svg
                className="call-button__icon"
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="currentColor"
                width="24"
                height="24"
              >
                <path d="M6.62 10.79a15.053 15.053 0 006.59 6.59l2.2-2.2a1.003 1.003 0 011.01-.24c1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.1.31.03.66-.25 1.02l-2.2 2.2z" />
              </svg>
            </a>
          </div>
        </div>
      )}
    </div>
  )
}

Header.propTypes = {
  menu: PropTypes.array,
  displayLogoLinks: PropTypes.bool,
  isModal: PropTypes.bool,
  closeModalFunction: PropTypes.func,
  contents: PropTypes.object,
}

Header.defaultProps = {
  displayLogoLinks: false,
  isModal: false,
}

export default Header
